/* ==========================================================================
   jv-scheduler.js - the ONE re-skinnable calendar/scheduler component (client).
   Phase A foundation (additive, dormant). Renders the scheduling contract
   (DOCS/scheduling-contract.md). Three modes (book / view / mark), two skins
   (admin / bride). Consumes an Availability/DaySlots payload + emits a
   { date, time } pick; it calls NO live endpoint in Phase A. Phase B wires each
   surface to JV_Schedule via the fetchMonth / fetchDay callbacks.

   Canonical conventions (must match DOCS/scheduling-contract.md + JV_ScheduleDate.gs):
     - Timezone Australia/Sydney always: "today" via
       toLocaleDateString('en-CA', { timeZone: 'Australia/Sydney' }).
     - Day-of-week Sun=0; the month grid is Monday-first using offset
       (firstDow + 6) % 7.

   API:
     JvScheduler.mount(el, {
       mode:   'book' | 'view' | 'mark',
       skin:   'admin' | 'bride',
       payload: { month, serviceName, days, slotsByDate, events, blocks, monthsData },
       fetchMonth: function(yyyyMm) -> Promise<{days, events, blocks}>   // optional
       fetchDay:   function(yyyyMmDd) -> Promise<{slots:[{time,label,available}]}> // optional
       onPick:     function({date, time})        // book mode
       onMark:     function({date, blocked})      // mark mode
       onDaySelect:function({date})               // any mode
     })
     JvScheduler.sydneyToday() -> 'YYYY-MM-DD'

   Also auto-mounts any [data-jv-scheduler] element with an embedded
   <script type="application/json" class="jv-scheduler-payload"> on DOMContentLoaded.
   ========================================================================== */

(function () {
  'use strict';
  if (window.JvScheduler && window.JvScheduler._v) return; // load-once guard

  var MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];
  var DOW_MON_FIRST = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  /** Today's date in Sydney as 'YYYY-MM-DD' (canonical client "today"). */
  function sydneyToday() {
    try {
      return new Date().toLocaleDateString('en-CA', { timeZone: 'Australia/Sydney' });
    } catch (e) {
      // Fallback: en-CA already yields YYYY-MM-DD; if timeZone unsupported, use local.
      var d = new Date();
      return d.getFullYear() + '-' + ('0' + (d.getMonth() + 1)).slice(-2) + '-' + ('0' + d.getDate()).slice(-2);
    }
  }

  function pad2(n) { return ('0' + n).slice(-2); }
  function isoOf(y, mIx, d) { return y + '-' + pad2(mIx + 1) + '-' + pad2(d); }
  function parseMonth(yyyyMm) { var p = String(yyyyMm).split('-'); return { y: +p[0], mIx: +p[1] - 1 }; }
  function addMonth(yyyyMm, delta) {
    var pm = parseMonth(yyyyMm); var m = pm.mIx + delta; var y = pm.y;
    while (m < 0) { m += 12; y--; } while (m > 11) { m -= 12; y++; }
    return y + '-' + pad2(m + 1);
  }
  /** Day-of-week Sun=0 for an ISO date, timezone-safe (noon-UTC anchor). */
  function dowSunZero(iso) {
    var p = iso.split('-');
    var dt = new Date(Date.UTC(+p[0], +p[1] - 1, +p[2], 12, 0, 0));
    return dt.getUTCDay(); // 0=Sun..6=Sat (UTC noon == same calendar day)
  }
  function el(tag, cls, attrs) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (attrs) Object.keys(attrs).forEach(function (k) { n.setAttribute(k, attrs[k]); });
    return n;
  }
  function chevron(dir) {
    var d = dir === 'left' ? 'M15 18l-6-6 6-6' : 'M9 18l6-6-6-6';
    return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" ' +
      'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="' + d + '"/></svg>';
  }

  function Scheduler(root, opts) {
    this.root = root;
    this.opts = opts || {};
    this.mode = this.opts.mode || root.getAttribute('data-mode') || 'book';
    this.skin = this.opts.skin || root.getAttribute('data-skin') || 'admin';
    this.payload = this.opts.payload || {};
    this.today = (this.payload.today) || sydneyToday();
    this.month = this.payload.month || this.today.slice(0, 7);
    this.selectedDate = null;
    this.selectedTime = null;
    this.localBlocks = {}; // mark-mode local overrides keyed by iso -> bool
    this._cache = {};      // month data cache keyed by yyyyMm
    this._renderToken = 0; // bumped on every _render(); a stale async fetchMonth response
                            // checks this before painting, so rapid nav clicks can never
                            // race the grid into a stuck/mismatched state (found live,
                            // RESULTS-experience-audit-booking-2026-08-09.md B-02).
    this._render();
  }

  Scheduler.prototype._monthData = function (yyyyMm, cb) {
    var self = this;
    if (this._cache[yyyyMm]) { cb(this._cache[yyyyMm]); return; }
    if (typeof this.opts.fetchMonth === 'function') {
      var p = this.opts.fetchMonth(yyyyMm);
      if (p && typeof p.then === 'function') {
        p.then(function (data) { self._cache[yyyyMm] = data || {}; cb(self._cache[yyyyMm]); })
         .catch(function () { cb({ days: {}, _error: true }); });
        return;
      }
      this._cache[yyyyMm] = p || {}; cb(this._cache[yyyyMm]); return;
    }
    // Static payload path.
    var md = this.payload.monthsData && this.payload.monthsData[yyyyMm];
    if (!md && yyyyMm === this.payload.month) {
      md = { days: this.payload.days || {}, events: this.payload.events || {}, blocks: this.payload.blocks || {} };
    }
    this._cache[yyyyMm] = md || { days: {} };
    cb(this._cache[yyyyMm]);
  };

  Scheduler.prototype._dayData = function (iso, cb) {
    if (typeof this.opts.fetchDay === 'function') {
      var p = this.opts.fetchDay(iso);
      if (p && typeof p.then === 'function') {
        p.then(function (d) { cb(d || { slots: [] }); }).catch(function () { cb({ slots: [], _error: true }); });
        return;
      }
      cb(p || { slots: [] }); return;
    }
    var byDate = this.payload.slotsByDate || {};
    cb({ slots: byDate[iso] || [] });
  };

  Scheduler.prototype._render = function () {
    var self = this;
    var r = this.root;
    var myToken = ++this._renderToken; // any earlier in-flight _monthData response is now stale
    r.className = 'jv-scheduler jv-scheduler--' + this.skin;
    r.setAttribute('data-mode', this.mode);
    r.setAttribute('role', 'group');
    r.setAttribute('aria-label', 'Scheduler');
    r.innerHTML = '';

    // Header
    var head = el('div', 'jvs-head');
    var titles = el('div', 'jvs-head-titles');
    var eyebrow = el('div', 'jvs-eyebrow');
    eyebrow.textContent = this.payload.serviceName || ({ book: 'Choose a date', view: 'Schedule', mark: 'Set availability' }[this.mode] || '');
    var pm = parseMonth(this.month);
    var monthTitle = el('h2', 'jvs-month'); monthTitle.textContent = MONTHS[pm.mIx] + ' ' + pm.y;
    titles.appendChild(eyebrow); titles.appendChild(monthTitle);
    var nav = el('div', 'jvs-nav');
    var prev = el('button', 'jvs-nav-btn', { type: 'button', 'aria-label': 'Previous month' }); prev.innerHTML = chevron('left');
    var next = el('button', 'jvs-nav-btn', { type: 'button', 'aria-label': 'Next month' }); next.innerHTML = chevron('right');
    // Guard against rapid double-click. A click during an in-flight month load is ignored
    // outright (buttons are disabled below, for the ~1-2s normal case and the rare slow/live
    // case alike), rather than firing a second overlapping _render()/fetchMonth() cycle that
    // can deadlock the grid on "Finding your available dates..." with no recovery but reload
    // (found live, RESULTS-experience-audit-booking-2026-08-09.md B-02).
    prev.addEventListener('click', function () {
      if (self._loading) return;
      self.month = addMonth(self.month, -1); self.selectedDate = null; self._render();
    });
    next.addEventListener('click', function () {
      if (self._loading) return;
      self.month = addMonth(self.month, 1); self.selectedDate = null; self._render();
    });
    nav.appendChild(prev); nav.appendChild(next);
    head.appendChild(titles); head.appendChild(nav);
    r.appendChild(head);
    this._prevBtn = prev; this._nextBtn = next;

    // Weekday header (Monday-first)
    var dow = el('div', 'jvs-dow');
    DOW_MON_FIRST.forEach(function (d) { var s = el('span'); s.textContent = d; dow.appendChild(s); });
    r.appendChild(dow);

    // Grid placeholder + panel
    var grid = el('div', 'jvs-grid', { role: 'grid', 'aria-label': MONTHS[pm.mIx] + ' ' + pm.y });
    r.appendChild(grid);
    var legend = el('div', 'jvs-legend'); r.appendChild(legend);
    var panel = el('div', 'jvs-panel', { hidden: 'hidden' }); r.appendChild(panel);
    this._grid = grid; this._panel = panel; this._legend = legend;

    // Paint a grid loading state BEFORE the (possibly slow, ~8-21s live) month fetch so
    // the grid never reads as empty / booked-out while availability is loading. For
    // precompute/static surfaces _monthData resolves synchronously and _renderGrid
    // replaces the skeleton in the same tick (no flash).
    this._renderGridLoading();
    this._loading = true;
    prev.disabled = true; next.disabled = true;
    // A live GAS round trip can itself hang (the underlying gasPost has its own 45s abort,
    // but a bride should not stare at a spinner that long). A client-side watchdog forces
    // recovery: if _monthData has not resolved in 20s, unlock the nav and show a retry state
    // rather than leaving the picker stuck forever with only a reload as an out.
    var watchdog = setTimeout(function () {
      if (self._renderToken !== myToken) return; // superseded already
      self._loading = false;
      if (self._prevBtn) self._prevBtn.disabled = false;
      if (self._nextBtn) self._nextBtn.disabled = false;
      self._renderGridTimeout();
    }, 20000);
    this._monthData(this.month, function (data) {
      // A newer _render() (from a click that landed after this one was already resolving,
      // or the watchdog firing) has already taken over. Never paint a stale response.
      if (self._renderToken !== myToken) return;
      clearTimeout(watchdog);
      self._loading = false;
      if (self._prevBtn) self._prevBtn.disabled = false;
      if (self._nextBtn) self._nextBtn.disabled = false;
      self._renderGrid(data); self._renderLegend();
    });
  };

  // Shown only if a month load exceeds the client-side watchdog above. Offers a real retry
  // (re-fires _render() for the current month) instead of the prior silent-forever spinner.
  Scheduler.prototype._renderGridTimeout = function () {
    var grid = this._grid; if (!grid) return;
    var self = this;
    grid.innerHTML = '';
    grid.removeAttribute('aria-busy');
    var wrap = el('div', 'jvs-grid-loading', { role: 'status', 'aria-live': 'polite' });
    var txt = el('span', 'jvs-loading-text'); txt.textContent = 'That took longer than expected.';
    var retry = el('button', 'jvs-nav-btn', { type: 'button' }); retry.textContent = 'Try again';
    retry.style.marginLeft = '0.75rem';
    retry.addEventListener('click', function () { self._render(); });
    wrap.appendChild(txt); wrap.appendChild(retry);
    grid.appendChild(wrap);
  };

  // Skeleton + "Finding your available dates..." shown while month availability loads.
  Scheduler.prototype._renderGridLoading = function () {
    var grid = this._grid; if (!grid) return;
    grid.innerHTML = '';
    grid.setAttribute('aria-busy', 'true');
    var status = el('div', 'jvs-grid-loading', { role: 'status', 'aria-live': 'polite' });
    var spinner = el('span', 'jvs-spinner', { 'aria-hidden': 'true' });
    var txt = el('span', 'jvs-loading-text'); txt.textContent = 'Finding your available dates...';
    status.appendChild(spinner); status.appendChild(txt);
    grid.appendChild(status);
    // Shimmer day-cells so the grid keeps its shape (6 weeks worth).
    for (var i = 0; i < 35; i++) grid.appendChild(el('div', 'jvs-cell jvs-cell-skeleton', { 'aria-hidden': 'true' }));
  };

  Scheduler.prototype._renderGrid = function (data) {
    var self = this;
    var grid = this._grid; grid.innerHTML = ''; grid.removeAttribute('aria-busy');
    var days = (data && data.days) || {};
    var events = (data && data.events) || {};
    var blocks = (data && data.blocks) || {};
    var pm = parseMonth(this.month);
    var firstIso = isoOf(pm.y, pm.mIx, 1);
    var firstDow = dowSunZero(firstIso);
    var offset = (firstDow + 6) % 7; // Monday-first leading blanks
    var daysInMonth = new Date(pm.y, pm.mIx + 1, 0).getDate();

    for (var b = 0; b < offset; b++) {
      grid.appendChild(el('div', 'jvs-cell is-empty', { 'aria-hidden': 'true' }));
    }

    var firstFocusable = null;
    for (var d = 1; d <= daysInMonth; d++) {
      var iso = isoOf(pm.y, pm.mIx, d);
      var status = days[iso] || 'unavail';
      var cell = el('button', 'jvs-cell', { type: 'button', role: 'gridcell', 'data-date': iso });

      var interactive = false;
      if (this.mode === 'book') {
        if (status === 'available') { cell.classList.add('is-available'); interactive = true; }
        else if (status === 'request') { cell.classList.add('is-request'); interactive = true; }
        else if (status === 'dayFull') cell.classList.add('is-dayfull');
        else if (status === 'past') cell.classList.add('is-past');
        else cell.classList.add('is-unavail');
      } else if (this.mode === 'view') {
        if (days[iso]) cell.classList.add('is-' + (status === 'available' ? 'available' : status === 'request' ? 'request' : 'unavail'));
        if ((events[iso] || []).length) interactive = true;
      } else if (this.mode === 'mark') {
        var blocked = (iso in this.localBlocks) ? this.localBlocks[iso] : !!(blocks[iso] && blocks[iso].allDay);
        cell.classList.add(blocked ? 'is-blocked' : 'is-markable');
        interactive = true;
      }

      if (iso === this.today) cell.classList.add('is-today');
      if (iso === this.selectedDate) cell.classList.add('is-selected');

      var num = el('span'); num.textContent = String(d); cell.appendChild(num);

      // affordance tag / dots
      if (this.mode === 'book') {
        if (status === 'dayFull') { var t1 = el('span', 'jvs-tag'); t1.textContent = 'Full'; cell.appendChild(t1); }
        else if (status === 'request') { var t2 = el('span', 'jvs-tag'); t2.textContent = 'Request'; cell.appendChild(t2); }
      } else if (this.mode === 'view') {
        var evs = events[iso] || [];
        if (evs.length) {
          var dots = el('div', 'jvs-dots');
          for (var k = 0; k < Math.min(evs.length, 3); k++) dots.appendChild(el('span', 'jvs-dot'));
          cell.appendChild(dots);
        }
      } else if (this.mode === 'mark') {
        var isB = cell.classList.contains('is-blocked');
        var tg = el('span', 'jvs-tag'); tg.textContent = isB ? 'Blocked' : 'Open'; cell.appendChild(tg);
        cell.setAttribute('aria-pressed', isB ? 'true' : 'false');
      }

      cell.setAttribute('aria-label', this._cellLabel(iso, status, d, pm));
      if (!interactive) { cell.setAttribute('aria-disabled', 'true'); cell.tabIndex = -1; }
      else {
        cell.tabIndex = (this.selectedDate === iso || (!this.selectedDate && !firstFocusable)) ? 0 : -1;
        if (!firstFocusable) firstFocusable = cell;
        (function (isoLocal, statusLocal) {
          cell.addEventListener('click', function () { self._onCellActivate(isoLocal, statusLocal); });
        })(iso, status);
      }
      grid.appendChild(cell);
    }

    this._wireKeyboard(grid);

    // Empty-month notice (opt-in via opts.emptyMonthMessage). Paging past the end of
    // the booking window renders a grid with nothing selectable and no explanation, so
    // a bride cannot tell "we don't take bookings this far ahead" from "something
    // broke". firstFocusable is the honest signal: it is set only when at least one
    // day is actually interactive. Opt-in so surfaces that legitimately show a
    // no-availability month (admin 'view'/'mark') are unaffected.
    if (this.mode === 'book' && !firstFocusable && this.opts.emptyMonthMessage) {
      var note = el('div', 'jvs-empty jvs-empty-month', { role: 'status', 'aria-live': 'polite' });
      note.textContent = this.opts.emptyMonthMessage;
      grid.parentNode.insertBefore(note, grid.nextSibling);
    }

    if (this.selectedDate && this.mode === 'book') this._renderSlots(this.selectedDate);
  };

  Scheduler.prototype._cellLabel = function (iso, status, d, pm) {
    var base = DOW_MON_FIRST[(dowSunZero(iso) + 6) % 7] + ' ' + d + ' ' + MONTHS[pm.mIx] + ' ' + pm.y;
    if (this.mode === 'book') {
      if (status === 'available') return base + ', available';
      if (status === 'request') return base + ', request only';
      if (status === 'dayFull') return base + ', fully booked';
      return base + ', unavailable';
    }
    if (this.mode === 'mark') return base + ', toggle availability';
    return base;
  };

  Scheduler.prototype._onCellActivate = function (iso, status) {
    var self = this;
    if (this.mode === 'mark') {
      var cur = (iso in this.localBlocks) ? this.localBlocks[iso] : false;
      // Controlled mode is used by authoritative availability editors. It
      // opens the detail/confirmation flow without painting an unsaved state.
      if (this.opts.controlledMark === true) {
        this.selectedDate = iso;
        this._emit('day-select', { date: iso, blocked: cur });
        if (typeof this.opts.onDaySelect === 'function') this.opts.onDaySelect({ date: iso, blocked: cur });
        return;
      }
      this.localBlocks[iso] = !cur;
      this._emit('mark', { date: iso, blocked: this.localBlocks[iso] });
      if (typeof this.opts.onMark === 'function') this.opts.onMark({ date: iso, blocked: this.localBlocks[iso] });
      // re-render grid to reflect new blocked state
      this._monthData(this.month, function (data) { self._renderGrid(data); });
      return;
    }
    this.selectedDate = iso;
    this.selectedTime = null;
    this._emit('day-select', { date: iso });
    if (typeof this.opts.onDaySelect === 'function') this.opts.onDaySelect({ date: iso });
    // update selected highlight without a full re-fetch
    var cells = this._grid.querySelectorAll('.jvs-cell');
    for (var i = 0; i < cells.length; i++) cells[i].classList.toggle('is-selected', cells[i].getAttribute('data-date') === iso);
    if (this.mode === 'book') this._renderSlots(iso);
    else if (this.mode === 'view') this._renderDayEvents(iso);
  };

  Scheduler.prototype._renderSlots = function (iso) {
    var self = this;
    var panel = this._panel; panel.hidden = false; panel.innerHTML = '';
    var title = el('h3', 'jvs-panel-title'); title.textContent = this._longDate(iso); panel.appendChild(title);
    // Text confirmation of the selected time (2026-09-17, external UX review + Jess's
    // own live click-through: a colour change alone on the pill was not reading as
    // "selected" to a real user -- add an unmissable, explicit sentence, not just a
    // stronger visual state on the pill itself). Hidden until a slot is picked;
    // selectSlot_ below shows/updates it. role="status" so it's announced to screen
    // readers the same moment a sighted user sees it change.
    var confirmEl = el('div', 'jvs-slot-confirm', { role: 'status', 'aria-live': 'polite' });
    confirmEl.hidden = true;
    panel.appendChild(confirmEl);
    // Animated loading state: instant, calm, MOVING feedback so a cold-start wait
    // reads as "working", not "frozen". Soft rose spinner + reassuring copy that
    // warms after a few seconds + a skeleton of slot pills so the area keeps shape.
    var loading = el('div', 'jvs-loading', { role: 'status', 'aria-live': 'polite' });
    var spinner = el('span', 'jvs-spinner', { 'aria-hidden': 'true' });
    var loadingText = el('span', 'jvs-loading-text');
    loadingText.textContent = 'Finding your available times...';
    loading.appendChild(spinner); loading.appendChild(loadingText);
    panel.appendChild(loading);

    var skel = el('div', 'jvs-skeleton', { 'aria-hidden': 'true' });
    for (var si = 0; si < 6; si++) skel.appendChild(el('span', 'jvs-skeleton-pill'));
    panel.appendChild(skel);

    // After a few seconds, warm the copy so a long wait never feels stuck.
    var reassureTimer = setTimeout(function () {
      if (loadingText.parentNode) loadingText.textContent = 'Almost there...';
    }, 6000);

    this._dayData(iso, function (day) {
      clearTimeout(reassureTimer);
      if (loading.parentNode) loading.parentNode.removeChild(loading);
      if (skel.parentNode) skel.parentNode.removeChild(skel);
      if (day && (day._error || day.availabilityError)) {
        var err = el('div', 'jvs-error'); err.textContent = 'We could not load times just now. Please try again in a moment.'; panel.appendChild(err); return;
      }
      var slots = (day && day.slots) || [];
      var avail = slots.filter(function (s) { return s.available; });
      if (!avail.length) { var e2 = el('div', 'jvs-empty'); e2.textContent = 'No times available on this day.'; panel.appendChild(e2); return; }
      var groups = [
        { label: 'Morning', test: function (m) { return m < 720; } },
        { label: 'Afternoon', test: function (m) { return m >= 720 && m < 1020; } },
        { label: 'Evening', test: function (m) { return m >= 1020; } }
      ];
      groups.forEach(function (g) {
        var inGroup = slots.filter(function (s) { return g.test(self._toMin(s.time)); });
        if (!inGroup.length) return;
        var wrap = el('div', 'jvs-slot-group');
        var gl = el('div', 'jvs-slot-group-label'); gl.textContent = g.label; wrap.appendChild(gl);
        var row = el('div', 'jvs-slots');
        inGroup.forEach(function (s) {
          var btn = el('button', 'jvs-slot', { type: 'button' });
          var btnLabel = s.label || s.time;
          btn.textContent = btnLabel;
          if (!s.available) { btn.disabled = true; btn.setAttribute('aria-disabled', 'true'); }
          else {
            btn.addEventListener('click', function () {
              self.selectedTime = s.time;
              var all = panel.querySelectorAll('.jvs-slot');
              for (var i = 0; i < all.length; i++) all[i].classList.remove('is-selected');
              btn.classList.add('is-selected');
              confirmEl.textContent = 'Your time: ' + btnLabel;
              confirmEl.hidden = false;
              self._emit('pick', { date: iso, time: s.time });
              if (typeof self.opts.onPick === 'function') self.opts.onPick({ date: iso, time: s.time });
            });
          }
          row.appendChild(btn);
        });
        wrap.appendChild(row); panel.appendChild(wrap);
      });
    });
  };

  Scheduler.prototype._renderDayEvents = function (iso) {
    var self = this;
    var panel = this._panel; panel.hidden = false; panel.innerHTML = '';
    var title = el('h3', 'jvs-panel-title'); title.textContent = this._longDate(iso); panel.appendChild(title);
    this._monthData(this.month, function (data) {
      var evs = ((data && data.events) || {})[iso] || [];
      if (!evs.length) { var e = el('div', 'jvs-empty'); e.textContent = 'Nothing scheduled.'; panel.appendChild(e); return; }
      evs.forEach(function (ev) {
        var row = el('div', 'jvs-event');
        var t = el('span', 'jvs-event-time'); t.textContent = ev.time || 'All day';
        var d = el('span'); d.textContent = ev.title || '';
        row.appendChild(t); row.appendChild(d); panel.appendChild(row);
      });
    });
  };

  Scheduler.prototype._renderLegend = function () {
    var lg = this._legend; lg.innerHTML = '';
    var items = [];
    if (this.mode === 'book') {
      items = [['s-available', 'Available'], ['s-request', 'Request only'], ['s-unavail', 'Unavailable'], ['s-selected', 'Selected']];
    } else if (this.mode === 'view') {
      items = [['s-available', 'Has openings'], ['s-unavail', 'Booked / off']];
    } else {
      items = [['s-unavail', 'No explicit block'], ['s-selected', 'Explicit block']];
    }
    items.forEach(function (it) {
      var s = el('span');
      s.innerHTML = '<span class="jvs-swatch ' + it[0] + '"></span>' + it[1];
      lg.appendChild(s);
    });
  };

  Scheduler.prototype._wireKeyboard = function (grid) {
    grid.addEventListener('keydown', function (e) {
      var keys = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
      if (keys.indexOf(e.key) === -1) return;
      var focusables = Array.prototype.filter.call(grid.querySelectorAll('.jvs-cell'), function (c) {
        return c.tabIndex === 0 || (c.getAttribute('role') === 'gridcell' && c.getAttribute('aria-disabled') !== 'true');
      });
      var cur = document.activeElement;
      var idx = focusables.indexOf(cur);
      if (idx === -1) return;
      e.preventDefault();
      var next = idx;
      if (e.key === 'ArrowLeft') next = Math.max(0, idx - 1);
      else if (e.key === 'ArrowRight') next = Math.min(focusables.length - 1, idx + 1);
      else if (e.key === 'ArrowUp') next = Math.max(0, idx - 7);
      else if (e.key === 'ArrowDown') next = Math.min(focusables.length - 1, idx + 7);
      else if (e.key === 'Home') next = 0;
      else if (e.key === 'End') next = focusables.length - 1;
      focusables.forEach(function (c) { c.tabIndex = -1; });
      focusables[next].tabIndex = 0;
      focusables[next].focus();
    });
  };

  Scheduler.prototype._toMin = function (hhmm) { var p = String(hhmm).split(':'); return (+p[0]) * 60 + (+p[1] || 0); };
  Scheduler.prototype._longDate = function (iso) {
    var p = iso.split('-'); var d = +p[2]; var pm = { y: +p[0], mIx: +p[1] - 1 };
    var dn = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][dowSunZero(iso)];
    return dn + ' ' + d + ' ' + MONTHS[pm.mIx] + ' ' + pm.y;
  };
  Scheduler.prototype._emit = function (name, detail) {
    try { this.root.dispatchEvent(new CustomEvent('jv-scheduler:' + name, { detail: detail, bubbles: true })); } catch (e) {}
  };

  function mount(node, opts) {
    if (!node || node._jvSchedMounted) return null;
    node._jvSchedMounted = true;
    return new Scheduler(node, opts || {});
  }

  function autoMount() {
    var nodes = document.querySelectorAll('[data-jv-scheduler]:not([data-jv-mounted])');
    Array.prototype.forEach.call(nodes, function (node) {
      node.setAttribute('data-jv-mounted', '1');
      var payload = {};
      var tag = node.querySelector('script.jv-scheduler-payload');
      if (tag) { try { payload = JSON.parse(tag.textContent || '{}'); } catch (e) { payload = {}; } }
      mount(node, {
        mode: node.getAttribute('data-mode') || 'book',
        skin: node.getAttribute('data-skin') || 'admin',
        payload: payload
      });
    });
  }

  window.JvScheduler = { _v: 1, mount: mount, autoMount: autoMount, sydneyToday: sydneyToday };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', autoMount);
  else autoMount();
})();
